import { Pencil } from "lucide-react";
import { StyleSheet, View, Text, Pressable } from "react-native";
import React, { useState } from "react";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { ReactNode } from "react";

import { AttachmentChip, AttachmentChipProps } from "../attachmentChip/AttachmentChip";
import { TaskChip, TaskChipProps } from "../taskChip/TaskChip";
import { colors, radii, spacing, typographyStyles } from "../../../../styles/design-system";
import CustomMarkdown from "../../../common/general/CustomMarkdown/CustomMarkdown";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import Tooltip from "../../../common/tooltip/Tooltip";
import GrowingTextInput from "../../chatLayout/messageComposer/GrowingTextInput";
import { MessageActionBar } from "./MessageActionBar";
import type { ReadAloudControl } from "./ReadAloudPlaybackControl";
import { stripMarkdownForTts } from "../../../../utils/stripMarkdownForTts";

export interface MessageBubbleProps {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  taskAttachmentChips?: AttachmentChipProps[];
  taskChips?: TaskChipProps[];
  onFeedback?: (value: "up" | "down" | null) => void;
  betweenBubbleAndActions?: ReactNode;
  feedback?: "up" | "down";
  readAloudControl?: ReadAloudControl;
  hideReadAloudControl?: boolean;
  isArchived: boolean;
  onEdit?: () => void;
  isBeingEdited?: boolean;
  onCancelEdit?: () => void;
  editValue?: string;
  onChangeEditValue?: (value: string) => void;
  onSaveEdit?: () => void;
  branches?: Array<{ id: string; created: string; anchorMessageId: string }>;
  selectedBranchId?: string | null;
  onSwitchBranch?: (anchorMessageId: string, targetBranchId: string | null) => void;
}

export const MessageBubble = ({
  content,
  role,
  timestamp,
  taskAttachmentChips = [],
  taskChips = [],
  betweenBubbleAndActions,
  onFeedback,
  feedback,
  readAloudControl,
  hideReadAloudControl = false,
  isArchived,
  onEdit,
  isBeingEdited = false,
  onCancelEdit,
  editValue,
  onChangeEditValue,
  onSaveEdit,
  branches,
  selectedBranchId,
  onSwitchBranch,
}: MessageBubbleProps) => {
  const [isEditButtonHovered, setIsEditButtonHovered] = useState(false);
  const handleEdit = () => {
    setIsEditButtonHovered(false);
    onEdit?.();
  };

  const goToPreviousVersion = () => {
    if (!branches || branches.length === 0 || !onSwitchBranch) {
      return;
    }
    if (!selectedBranchId) {
      // Already on base version; nothing to do.
      return;
    }

    const currentIndex = branches.findIndex((b) => b.id === selectedBranchId);
    const isOnOrBeforeFirstBranch = currentIndex <= 0;

    if (isOnOrBeforeFirstBranch) {
      // Move back to base (original) version.
      onSwitchBranch(branches[0].anchorMessageId, null);
      return;
    }

    const targetBranchId = branches[currentIndex - 1]?.id ?? null;
    onSwitchBranch(branches[0].anchorMessageId, targetBranchId);
  };

  const goToNextVersion = () => {
    if (!branches || branches.length === 0 || !onSwitchBranch) {
      return;
    }

    if (!selectedBranchId) {
      // From base, first click goes to the first branch.
      const firstBranch = branches[0];
      if (firstBranch) {
        onSwitchBranch(firstBranch.anchorMessageId, firstBranch.id);
      }
      return;
    }

    const currentIndex = branches.findIndex((b) => b.id === selectedBranchId);
    const isInvalidOrLastBranch = currentIndex < 0 || currentIndex >= branches.length - 1;
    if (isInvalidOrLastBranch) {
      return;
    }

    const targetBranchId = branches[currentIndex + 1]?.id;
    if (targetBranchId) {
      onSwitchBranch(branches[0].anchorMessageId, targetBranchId);
    }
  };

  const messageStyles = [styles.messageBubble, role === "user" ? styles.userMessage : styles.assistantMessage];
  const bubbleStyles = [
    styles.bubble,
    role === "user" ? styles.userBubble : styles.assistantBubble,
    role === "user" && isArchived && styles.userArchivedBubble,
  ];
  const messageWrapperStyles = [
    styles.messageWrapper,
    role === "user" ? styles.userMessageWrapper : styles.assistantMessageWrapper,
  ];
  const textStyles = [
    styles.message,
    typographyStyles.textBase,
    role === "user" ? styles.userText : styles.assistantText,
    role === "user" && isArchived && styles.userArchivedText,
  ];
  const showEditButton = !isArchived && role === "user" && !!onEdit && !isBeingEdited;
  const messageAccessibilityLabel = stripMarkdownForTts(content) || content;

  return (
    <View style={messageStyles}>
      <View style={messageWrapperStyles}>
        {(taskAttachmentChips ?? []).map((attachment, index) => (
          <AttachmentChip key={index} {...attachment} removeChip={isArchived ? undefined : attachment?.removeChip} />
        ))}
        <View style={role === "user" ? styles.bubbleRow : undefined}>
          <View style={isBeingEdited && role === "user" ? styles.inlineEditBubble : bubbleStyles}>
            {isBeingEdited && role === "user" ? (
              <View style={styles.inlineEditContainer}>
                <GrowingTextInput
                  value={editValue ?? content}
                  setValue={(value) => onChangeEditValue?.(value)}
                  placeholder="Edit message..."
                  onKeySubmit={onSaveEdit}
                  minHeight={36}
                  maxHeight={140}
                />
                <View style={styles.inlineEditActions}>
                  <CustomButton
                    text="Cancel"
                    useTranslationText={false}
                    onPress={onCancelEdit}
                    styleType="secondary"
                    size="s"
                    aria-label="Cancel edit"
                    accessibilityLabel="Cancel edit"
                  />
                  <CustomButton
                    text="Send"
                    useTranslationText={false}
                    onPress={onSaveEdit}
                    styleType="primary"
                    size="s"
                    aria-label="Save edit"
                    accessibilityLabel="Save edit"
                  />
                </View>
              </View>
            ) : (
              <>
                <View
                  accessible
                  accessibilityRole="text"
                  accessibilityLabel={messageAccessibilityLabel}
                  tabIndex={0}
                  style={styles.messageTextContainer}>
                  <CustomMarkdown
                    style={{
                      body: textStyles,
                      math_inline: [
                        role === "user" && styles.userText,
                        { fontSize: typographyStyles.textBase.fontSize },
                      ],
                      math_block: [
                        role === "user" && styles.userText,
                        { fontSize: typographyStyles.textBase.fontSize },
                      ],
                      ...(role === "user" && {
                        paragraph: { marginTop: 0, marginBottom: 0 },
                      }),
                    }}
                    content={content}
                    suppressContainerAccessibility
                  />
                </View>
                {(taskChips ?? []).map((attachment, index) => (
                  <View key={index} style={{ marginTop: spacing.sm }}>
                    <TaskChip {...attachment} />
                  </View>
                ))}
              </>
            )}
          </View>
        </View>
        {betweenBubbleAndActions ? (
          <View style={styles.betweenBubbleAndActionsContainer}>{betweenBubbleAndActions}</View>
        ) : null}
        <View style={styles.actionBar}>
          {branches && branches.length > 0 && onSwitchBranch && (
            <View style={styles.branchSwitcher}>
              <Pressable onPress={goToPreviousVersion} disabled={!selectedBranchId} style={styles.branchSwitchButton}>
                <CaretLeftIcon size={12} color={!selectedBranchId ? colors.grey[300] : colors.grey[600]} />
              </Pressable>
              <Text style={styles.branchSwitchText}>
                {selectedBranchId
                  ? // Base is 1, branches are 2..N+1
                    branches.findIndex((b) => b.id === selectedBranchId) + 2
                  : 1}
                /{branches.length + 1}
              </Text>
              <Pressable
                onPress={goToNextVersion}
                disabled={
                  // Disable if there are no branches, or we're already on the last branch.
                  branches.length === 0 ||
                  (selectedBranchId && branches.findIndex((b) => b.id === selectedBranchId) >= branches.length - 1)
                }
                style={styles.branchSwitchButton}>
                <CaretRightIcon
                  size={12}
                  color={
                    branches.length === 0 ||
                    (selectedBranchId && branches.findIndex((b) => b.id === selectedBranchId) >= branches.length - 1)
                      ? colors.grey[300]
                      : colors.grey[600]
                  }
                />
              </Pressable>
            </View>
          )}
          {showEditButton && (
            <Tooltip content="Edit message" placement="bottom" offset={8} showArrow={false}>
              <View style={styles.actionButtonGroup}>
                <CustomButton
                  onPress={handleEdit}
                  onHover={setIsEditButtonHovered}
                  styleType="transparent"
                  size="s"
                  leftIcon={<Pencil size={14} color={isEditButtonHovered ? colors.grey[700] : colors.grey[400]} />}
                  aria-label="Edit message"
                  accessibilityLabel="Edit message"
                />
              </View>
            </Tooltip>
          )}
          {role === "assistant" && (
            <MessageActionBar
              content={content}
              feedback={feedback}
              onFeedback={!isArchived ? onFeedback : undefined}
              readAloudControl={readAloudControl}
              hideReadAloudControl={hideReadAloudControl}
            />
          )}
          {role === "user" ? (
            <Text style={styles.timestamp}>
              {new Date(timestamp).toLocaleString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  messageBubble: {
    alignSelf: "stretch",
    display: "flex",
    alignItems: "flex-start",
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  userMessage: {
    flexDirection: "row-reverse",
  },
  assistantMessage: {
    flexDirection: "row",
  },
  bubble: {
    borderRadius: radii.lg,
    maxWidth: "90%",
  },
  userBubble: {
    padding: spacing.sm,
    borderTopRightRadius: radii.sm,
    backgroundColor: colors.brand[500],
  },
  userArchivedBubble: {
    backgroundColor: colors.grey[400],
  },
  userArchivedText: {
    color: colors.white,
  },
  assistantBubble: {
    backgroundColor: "transparent",
  },
  inlineEditBubble: {
    borderRadius: radii.lg,
    width: "100%",
    maxWidth: "100%",
    flex: 1,
    alignSelf: "stretch",
    padding: spacing.sm,
    borderWidth: 3,
    borderColor: colors.brand[200],
    backgroundColor: colors.white,
  },
  inlineEditContainer: {
    flexDirection: "column",
    alignItems: "stretch",
    flex: 1,
    gap: spacing.xs,
  },
  messageWrapper: {
    display: "flex",
    flexGrow: 1,
    width: "100%",
    maxWidth: "100%",
  },
  userMessageWrapper: {
    alignItems: "flex-end",
  },
  assistantMessageWrapper: {
    alignItems: "flex-start",
  },
  message: {
    textWrap: "wrap",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    // @ts-expect-error - works for web
    display: "block",
  },
  userText: {
    textAlign: "right",
    color: colors.white,
  },
  assistantText: {
    textAlign: "left",
    color: colors.grey["700"],
    userSelect: "text",
    // @ts-expect-error - works for web
    display: "block",
  },
  messageTextContainer: {
    userSelect: "text",
  },
  actionBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: spacing.xs,
    marginTop: spacing.xxs,
    gap: spacing.xs,
    color: colors.grey[500],
    ...typographyStyles.textXs,
  },
  betweenBubbleAndActionsContainer: {
    width: "100%",
  },
  timestamp: {
    color: colors.grey[500],
    ...typographyStyles.textXs,
  },
  bubbleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    alignSelf: "stretch",
    maxWidth: "100%",
    gap: spacing.xxs,
  },
  actionButtonGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  inlineEditActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: spacing.xs,
    gap: spacing.sm,
  },
  branchSwitcher: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  branchSwitchButton: {
    padding: spacing.xxs,
  },
  branchSwitchText: {
    ...typographyStyles.textXs,
    color: colors.grey[600],
  },
});
